import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, X, Search, Camera, ChevronLeft, ChevronRight, Loader2, Sparkles, Share2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import Webcam from 'react-webcam';
import CollectionPills from '../components/gallery/cinema/CollectionPills';
import CinemaFooter from '../components/gallery/cinema/CinemaFooter';
import MasonryGrid from '../components/MasonryGrid';
import { trackVisit, trackIdentify, trackSave, visitorQuery, appendVisitorFields } from '../lib/visitorTracking';
import useSavePhoto, { filenameFromResponse } from '../hooks/useSavePhoto';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Branded gallery images must only be obtainable via the Download button (which returns a
// server-side branded composite). These props/styles block casual save paths: right-click
// context menu, drag-to-save, and iOS/Android long-press image sheets.
const noSaveImgProps = {
  draggable: false,
  onContextMenu: (e) => e.preventDefault(),
};
const noSaveImgStyle = {
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  userSelect: 'none',
};

const BrandedGallery = () => {
  const { brandedToken, collectionSlug } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [activeFrame, setActiveFrame] = useState(null);
  const [allFrames, setAllFrames] = useState([]);
  const [preRendered, setPreRendered] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [displayPhotos, setDisplayPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showFaceSearch, setShowFaceSearch] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  // When set, the live <Webcam> is replaced with the just-captured frame so
  // the user sees a frozen preview while the search request is in flight.
  const [capturedSelfie, setCapturedSelfie] = useState(null);
  const [uploadingReference, setUploadingReference] = useState(false);
  const [searchingFaces, setSearchingFaces] = useState(false);
  const [photoOrientations, setPhotoOrientations] = useState({});
  const [lightboxOrientations, setLightboxOrientations] = useState({});
  const [lightboxAspects, setLightboxAspects] = useState({}); // photoId → "W / H" string
  const [downloading, setDownloading] = useState(null);
  const [downloadingPhoto, setDownloadingPhoto] = useState(null); // full photo for the progress modal
  const [copiedCollectionLink, setCopiedCollectionLink] = useState(false);
  const { savePhoto, savePrompt } = useSavePhoto();
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);

  // Sorting and collection state
  const [sortOrder, setSortOrder] = useState('newest');
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(collectionSlug || null);

  // User info modal
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const userNameRef = useRef(userName);
  const userEmailRef = useRef(userEmail);
  useEffect(() => { userNameRef.current = userName; }, [userName]);
  useEffect(() => { userEmailRef.current = userEmail; }, [userEmail]);

  // Load saved user info
  useEffect(() => {
    const storedName = sessionStorage.getItem(`branded_gallery_${brandedToken}_name`);
    const storedEmail = sessionStorage.getItem(`branded_gallery_${brandedToken}_email`);
    if (storedName && storedEmail) {
      setUserName(storedName);
      setUserEmail(storedEmail);
    }
  }, [brandedToken]);

  // Track each photo's orientation from the grid thumbnails so the right
  // (portrait/landscape) frame overlay is chosen in the grid and lightbox.
  const handleGridImageLoad = useCallback((photoId, e) => {
    if (e?.target) {
      const { naturalWidth, naturalHeight } = e.target;
      setPhotoOrientations(prev => ({
        ...prev,
        [photoId]: naturalWidth >= naturalHeight ? 'landscape' : 'portrait'
      }));
    }
  }, []);

  const handleLightboxImageLoad = useCallback((photoId, e) => {
    if (e?.target) {
      const { naturalWidth, naturalHeight } = e.target;
      setLightboxOrientations(prev => ({
        ...prev,
        [photoId]: naturalWidth >= naturalHeight ? 'landscape' : 'portrait'
      }));
      setLightboxAspects(prev => ({
        ...prev,
        [photoId]: `${naturalWidth} / ${naturalHeight}`
      }));
    }
  }, []);

  // Resolve which frame applies to a given photo: per-collection frame > gallery default
  const resolveFrameForPhoto = (photo) => {
    if (!photo) return null;
    if (photo.collection_id && allFrames.length > 0 && collections.length > 0) {
      const coll = collections.find(c => c.id === photo.collection_id);
      if (coll?.frame_id) {
        const collFrame = allFrames.find(f => f.id === coll.frame_id);
        if (collFrame) return collFrame;
      }
    }
    return activeFrame;
  };

  const getFrameUrl = (photo) => {
    const frame = resolveFrameForPhoto(photo);
    if (!frame) return null;
    const orientation = photoOrientations[photo.id] || 'landscape';
    return orientation === 'portrait' ? frame.portrait_overlay_url : frame.landscape_overlay_url;
  };

  const getLightboxFrameUrl = (photo) => {
    const frame = resolveFrameForPhoto(photo);
    if (!frame) return null;
    const orientation = lightboxOrientations[photo.id] || photoOrientations[photo.id] || 'landscape';
    return orientation === 'portrait' ? frame.portrait_overlay_url : frame.landscape_overlay_url;
  };

  // Check user info
  const checkUserInfo = (pendingActionData) => {
    if (!userNameRef.current || !userEmailRef.current) {
      setPendingAction(pendingActionData);
      setShowUserInfoModal(true);
      return false;
    }
    return true;
  };

  const handleUserInfoSubmit = (e) => {
    e.preventDefault();
    if (!userName.trim() || !userEmail.trim()) {
      toast.error('Please enter both name and email');
      return;
    }
    sessionStorage.setItem(`branded_gallery_${brandedToken}_name`, userName);
    sessionStorage.setItem(`branded_gallery_${brandedToken}_email`, userEmail);
    trackIdentify(`${BACKEND_URL}/api/branded-gallery/${brandedToken}/track`, { name: userName, email: userEmail });
    setShowUserInfoModal(false);

    const action = pendingAction;
    setPendingAction(null);
    if (action) {
      if (action.type === 'download') performDownload(action.photo);
      else if (action.type === 'faceSearch') searchFacesWithFile(action.file);
    }
  };

  // Load gallery
  useEffect(() => {
    loadGallery();
    trackVisit(`${BACKEND_URL}/api/branded-gallery/${brandedToken}/track`, { collection: collectionSlug || null });
  }, [brandedToken]);

  useEffect(() => {
    setDisplayPhotos(photos);
  }, [photos]);

  // Face-match emails link here with ?subscription=<token>: open on that person's photos,
  // with their details already filled in so downloads don't ask again.
  const subscriptionToken = new URLSearchParams(window.location.search).get('subscription');
  const [subscriptionPhotoIds, setSubscriptionPhotoIds] = useState(null);

  useEffect(() => {
    if (!subscriptionToken) return;
    axios.get(`${BACKEND_URL}/api/branded-gallery/${brandedToken}/my-photos/${subscriptionToken}`)
      .then(({ data }) => {
        const name = data.name || data.email.split('@')[0];
        setUserName(name);
        setUserEmail(data.email);
        sessionStorage.setItem(`branded_gallery_${brandedToken}_name`, name);
        sessionStorage.setItem(`branded_gallery_${brandedToken}_email`, data.email);
        trackIdentify(`${BACKEND_URL}/api/branded-gallery/${brandedToken}/track`, { name, email: data.email });

        const ids = data.photo_ids || [];
        setSubscriptionPhotoIds(ids);
        if (ids.length) toast.success(`Showing ${ids.length} photo${ids.length === 1 ? '' : 's'} of you`);
        else toast.info('No photos of you yet. We\'ll email you when some are uploaded.');
      })
      .catch(() => toast.error('That photo link is no longer valid. Try a face search instead.'));
  }, [brandedToken, subscriptionToken]);

  // Declared after the reset above so it wins when photos (re)load.
  useEffect(() => {
    if (subscriptionPhotoIds?.length) {
      setDisplayPhotos(photos.filter((p) => subscriptionPhotoIds.includes(p.id)));
    }
  }, [photos, subscriptionPhotoIds]);

  const loadGallery = async (currentSortOrder = null) => {
    try {
      setLoading(true);
      const params = [];
      if (currentSortOrder) params.push(`sort=${currentSortOrder}`);
      if (collectionSlug) params.push(`collection=${collectionSlug}`);
      const qs = params.length ? `?${params.join('&')}` : '';

      // Meta + photos in parallel. When no sort is given, the backend applies the
      // gallery's configured default — the same value the meta response reports.
      const [galleryRes, photosRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/branded-gallery/${brandedToken}`),
        axios.get(`${BACKEND_URL}/api/branded-gallery/${brandedToken}/photos${qs}`),
      ]);
      setGallery(galleryRes.data);
      setActiveFrame(galleryRes.data.active_frame);
      setAllFrames(galleryRes.data.frames || []);
      setPreRendered(!!galleryRes.data.pre_rendered);
      setCollections(galleryRes.data.collections || []);
      setSortOrder(currentSortOrder || galleryRes.data.default_sort || 'newest');

      const photosData = Array.isArray(photosRes.data) ? photosRes.data : [];
      setPhotos(photosData);
      setDisplayPhotos(photosData);
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast.error('Gallery not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCollectionChange = async (newCollectionSlug, options = {}) => {
    const { skipNavigate = false } = options;
    setSelectedCollection(newCollectionSlug);

    if (!skipNavigate) {
      const targetPath = newCollectionSlug
        ? `/branded/${brandedToken}/c/${newCollectionSlug}`
        : `/branded/${brandedToken}`;
      navigate(targetPath, { replace: false });
    }

    try {
      const collParam = newCollectionSlug ? `&collection=${newCollectionSlug}` : '';
      const response = await axios.get(
        `${BACKEND_URL}/api/branded-gallery/${brandedToken}/photos?sort=${sortOrder}${collParam}`
      );
      const photosData = Array.isArray(response.data) ? response.data : [];
      setPhotos(photosData);
      setDisplayPhotos(photosData);
    } catch (error) {
      toast.error('Failed to filter photos');
    }
  };

  // Browser back/forward support
  useEffect(() => {
    const urlSlug = collectionSlug || null;
    if (urlSlug !== selectedCollection) {
      handleCollectionChange(urlSlug, { skipNavigate: true });
    }
  }, [collectionSlug]);

  const copyCollectionLink = async (slug) => {
    const url = `${window.location.origin}/branded/${brandedToken}/c/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCollectionLink(true);
      toast.success('Collection link copied');
      setTimeout(() => setCopiedCollectionLink(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleSortChange = async (newSortOrder) => {
    if (newSortOrder === sortOrder) return;
    setSortOrder(newSortOrder);
    try {
      const collParam = selectedCollection ? `&collection=${selectedCollection}` : '';
      const response = await axios.get(
        `${BACKEND_URL}/api/branded-gallery/${brandedToken}/photos?sort=${newSortOrder}${collParam}`
      );
      const photosData = Array.isArray(response.data) ? response.data : [];
      setPhotos(photosData);
      setDisplayPhotos(photosData);
    } catch (error) {
      toast.error('Failed to change sort order');
    }
  };

  // Face search
  const closeFaceSearchModal = () => {
    setShowFaceSearch(false);
    setShowWebcam(false);
    setCapturedSelfie(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleWebcamError = (err) => {
    console.error('Webcam error:', err);
    toast.error('Camera not available. Try "Upload a photo" instead.');
    setShowWebcam(false);
  };

  const handleCaptureSelfie = async () => {
    if (!webcamRef.current) return;
    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) { toast.error('Failed to capture photo'); return; }
      // Freeze the preview to the captured frame BEFORE awaiting the network
      // call — otherwise the live video keeps moving while we search.
      setCapturedSelfie(imageSrc);
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      await searchFacesWithFile(file);
    } catch (error) {
      toast.error('Failed to capture selfie');
      setCapturedSelfie(null);
    }
  };

  const handleReferencePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }
    await searchFacesWithFile(file);
  };

  const searchFacesWithFile = async (file) => {
    if (!checkUserInfo({ type: 'faceSearch', file })) return;
    setSubscriptionPhotoIds(null);
    setUploadingReference(true);
    setSearchingFaces(true);
    try {
      const formData = new FormData();
      formData.append('reference_photo', file);
      appendVisitorFields(formData, { name: userNameRef.current, email: userEmailRef.current });
      const response = await axios.post(
        `${BACKEND_URL}/api/branded-gallery/${brandedToken}/search/faces`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const matchedPhotos = response.data.matched_photos || [];
      if (matchedPhotos.length === 0) {
        toast.info('No matching faces found in this gallery');
        setDisplayPhotos(photos);
      } else {
        const matchedIds = matchedPhotos.map(m => m.photo_id);
        const filtered = photos.filter(p => matchedIds.includes(p.id));
        setDisplayPhotos(filtered);
        toast.success(`Found ${matchedPhotos.length} photo${matchedPhotos.length !== 1 ? 's' : ''} with matching faces!`);
      }
      // Close the modal once the search resolves (success or no matches).
      closeFaceSearchModal();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to search for faces');
      setDisplayPhotos(photos);
    } finally {
      setUploadingReference(false);
      setSearchingFaces(false);
    }
  };

  const clearFaceSearch = () => {
    setSubscriptionPhotoIds(null);
    setDisplayPhotos(photos);
    setShowFaceSearch(false);
    setShowWebcam(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    toast.success('Showing all photos');
  };

  // Download with frame
  const performDownload = async (photo) => {
    setDownloading(photo.id);
    // Only show the prep modal when the gallery isn't pre-rendered
    // (pre-rendered downloads return instantly from disk).
    if (!preRendered) {
      setDownloadingPhoto(photo);
    }
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/branded-gallery/${brandedToken}/photo/${photo.id}/download?size=social&${visitorQuery({ name: userNameRef.current, email: userEmailRef.current })}`,
        { responseType: 'blob' }
      );
      // The file is here; clear the progress UI before a possible "Save to Photos" prompt.
      setDownloading(null);
      setDownloadingPhoto(null);
      const name = photo.filename?.replace(/\.[^.]+$/, '') || 'photo';
      const outcome = await savePhoto(response.data, filenameFromResponse(response, `${name}_framed.jpg`));
      trackSave(`${BACKEND_URL}/api/branded-gallery/${brandedToken}/track`, {
        name: userNameRef.current, email: userEmailRef.current, photoId: photo.id, outcome,
      });
      if (outcome === 'downloaded') toast.success('Photo downloaded with frame!');
    } catch (error) {
      toast.error('Failed to download photo');
    } finally {
      setDownloading(null);
      setDownloadingPhoto(null);
    }
  };

  const downloadPhoto = async (photo, e) => {
    if (e) e.stopPropagation();
    if (!checkUserInfo({ type: 'download', photo })) return;
    await performDownload(photo);
  };

  // Lightbox
  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  const nextPhoto = () => setCurrentPhotoIndex((prev) => (prev + 1) % displayPhotos.length);
  const prevPhoto = () => setCurrentPhotoIndex((prev) => (prev - 1 + displayPhotos.length) % displayPhotos.length);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, displayPhotos.length]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#16161A' }}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 rounded-full animate-spin mb-4" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }}></div>
          <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Loading gallery…</p>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ background: '#16161A' }}>
        <div className="text-center px-6">
          <h1 className="font-tiq-display uppercase mb-2" style={{ fontWeight: 900, fontSize: 32, letterSpacing: '-0.02em' }}>Gallery not found</h1>
          <p style={{ color: 'rgba(255,255,255,0.55)' }}>This gallery does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const heroKicker = [
    gallery.event_date ? new Date(gallery.event_date).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : null,
    `${photos.length} ${photos.length === 1 ? 'Photo' : 'Photos'}`,
  ].filter(Boolean).join(' · ');
  // long names wrap gracefully (same rule as CinemaHero)
  const heroNameIsLong = (gallery.name || '').length > 22;

  return (
    <div className="min-h-screen font-tiq-body" style={{ background: '#16161A' }}>
      <Toaster position="top-center" richColors />
      {savePrompt}

      {/* User Info Modal */}
      {showUserInfoModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => { setShowUserInfoModal(false); setPendingAction(null); }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Gallery</h2>
            <p className="text-slate-600 mb-6">Please enter your details to download photos and use face search.</p>
            <form onSubmit={handleUserInfoSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BF212F]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BF212F]"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#BF212F] to-[#F26B38] text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-all shadow-lg"
              >
                Continue
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Face Search Modal (two-step: instructions → webcam) */}
      {showFaceSearch && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
          onClick={closeFaceSearchModal}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[95vh] overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeFaceSearchModal}
              className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-white text-slate-700 rounded-full p-2 transition-all"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {!showWebcam ? (
              <div className="p-5 sm:p-8">
                <div className="flex items-center gap-3 mb-4 pr-8">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#BF212F] to-[#F26B38] flex items-center justify-center shadow-lg shadow-red-500/20 flex-shrink-0">
                    <Search className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">Find Yourself in Photos</h3>
                </div>

                <p className="text-slate-600 text-sm mb-4">
                  Take a quick selfie — we'll match your face to every photo you appear in.
                </p>

                <div className="bg-slate-50 rounded-xl p-4 mb-5">
                  <p className="font-semibold text-slate-800 text-sm mb-2">For best results:</p>
                  <ul className="space-y-1.5 text-sm text-slate-600">
                    <li className="flex items-start gap-2"><span className="text-[#BF212F] mt-0.5">✓</span>Good, even lighting on your face</li>
                    <li className="flex items-start gap-2"><span className="text-[#BF212F] mt-0.5">✓</span>Look straight at the camera</li>
                    <li className="flex items-start gap-2"><span className="text-[#BF212F] mt-0.5">✓</span>No sunglasses or face coverings</li>
                    <li className="flex items-start gap-2"><span className="text-[#BF212F] mt-0.5">✓</span>Just your face in the frame</li>
                  </ul>
                </div>

                <button
                  onClick={() => setShowWebcam(true)}
                  disabled={uploadingReference || searchingFaces}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#BF212F] to-[#F26B38] text-white px-5 py-4 rounded-xl font-bold text-base hover:opacity-90 active:scale-[0.99] transition-all shadow-lg shadow-red-500/20 disabled:opacity-50 min-h-[52px]"
                >
                  <Camera className="w-5 h-5" />
                  <span>Take a Selfie</span>
                </button>

                <label className="mt-3 block text-center cursor-pointer py-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleReferencePhotoUpload}
                    disabled={uploadingReference || searchingFaces}
                    className="hidden"
                  />
                  <span className="text-sm text-slate-600 hover:text-[#BF212F] hover:underline font-medium">
                    {uploadingReference ? 'Searching…' : 'Or upload a photo instead'}
                  </span>
                </label>

                <p className="text-xs text-slate-400 text-center mt-4">
                  Your selfie is only used to match photos in this gallery.
                </p>
              </div>
            ) : (
              <div className="p-4 sm:p-6">
                <div className="mb-3 pr-8">
                  <h3 className="text-lg font-bold text-slate-900">Take your selfie</h3>
                  <p className="text-sm text-slate-500">Center your face and tap capture.</p>
                </div>

                <div className="relative rounded-xl overflow-hidden border-2 border-[#BF212F] bg-black aspect-[3/4] sm:aspect-[4/3] mb-4">
                  {capturedSelfie ? (
                    <>
                      {/* Frozen capture while the search request is in flight */}
                      <img
                        src={capturedSelfie}
                        alt="Captured selfie"
                        className="w-full h-full object-cover scale-x-[-1]"
                      />
                      {searchingFaces && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-white">
                          <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin mb-2" />
                          <p className="text-sm font-medium">Searching for matches…</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{
                          facingMode: 'user',
                          width: { ideal: 1280 },
                          height: { ideal: 1280 },
                        }}
                        onUserMediaError={handleWebcamError}
                        className="w-full h-full object-cover"
                        mirrored
                        playsInline
                      />
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="w-40 h-52 sm:w-48 sm:h-60 border-2 border-white/60 rounded-full shadow-[0_0_0_9999px_rgba(0,0,0,0.25)]" />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowWebcam(false)}
                    disabled={uploadingReference || searchingFaces}
                    className="px-3 sm:px-4 py-3 border border-slate-300 text-slate-700 rounded-xl font-medium text-sm hover:bg-slate-50 active:bg-slate-100 transition-colors disabled:opacity-50 min-h-[48px]"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleCaptureSelfie}
                    disabled={uploadingReference || searchingFaces}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#BF212F] to-[#F26B38] text-white px-5 py-3 rounded-xl font-bold hover:opacity-90 active:scale-[0.99] transition-all disabled:opacity-50 min-h-[48px]"
                  >
                    {searchingFaces || uploadingReference ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Searching…</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5" />
                        <span>Capture & Search</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full-bleed 100svh hero, matching CinemaHero's sizing — but white-label
          neutral: no Turbo logo or signal-wave watermark. */}
      <div className="relative w-full overflow-hidden" style={{ height: '100svh', minHeight: 600, background: '#1a1a1c' }}>
        {gallery.featured_photo_url ? (
          <>
            <div
              className="absolute inset-0 bg-cover"
              style={{
                backgroundImage: `url(${gallery.featured_photo_url})`,
                backgroundPosition: gallery.featured_photo_focal_point ? `${gallery.featured_photo_focal_point.x}% ${gallery.featured_photo_focal_point.y}%` : 'center',
                filter: 'brightness(0.8)',
              }}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 24%, rgba(0,0,0,0) 44%, rgba(0,0,0,0.85) 100%)' }} />
          </>
        ) : (
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,#BF212F,#8B1923)' }} />
        )}
        <div className="absolute inset-0 pointer-events-none" style={{ border: 'clamp(6px,1vw,10px) solid #fff' }} />
        <div className="absolute z-[3]" style={{ left: 'clamp(24px,4vw,60px)', right: 'clamp(24px,4vw,60px)', bottom: 'clamp(48px,8vh,76px)' }}>
          <div className="tiq-kicker" style={{ fontSize: 'clamp(11px,1.4vw,14px)', color: 'rgba(255,255,255,0.85)' }}>{heroKicker}</div>
          <div style={{ width: 54, height: 4, background: '#fff', margin: 'clamp(14px,2vh,20px) 0 clamp(12px,2vh,18px)' }} />
          <h1
            className="font-tiq-display text-white uppercase m-0"
            style={{
              fontWeight: 900,
              fontSize: heroNameIsLong ? 'clamp(34px,5.4vw,52px)' : 'clamp(42px,8.4vw,92px)',
              lineHeight: 0.92,
              letterSpacing: '-0.025em',
              maxWidth: heroNameIsLong ? '18ch' : '14ch',
              textWrap: 'balance',
            }}
          >
            {String(gallery.name || 'Gallery')}
          </h1>
        </div>
        <div className="absolute bottom-[22px] left-1/2 -translate-x-1/2 z-[3] flex flex-col items-center gap-1.5">
          <span className="tiq-kicker" style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>SCROLL</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" className="animate-bounce">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </div>

      {/* Prominent Find My Photos CTA (white) */}
      <div style={{ padding: 'clamp(20px,3vw,32px) clamp(16px,3vw,38px) 0' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <button
            onClick={() => setShowFaceSearch(true)}
            className="group font-tiq-heading w-full flex items-center justify-center gap-3"
            style={{ background: '#fff', color: '#0A0A0B', borderRadius: 14, padding: '16px 24px', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
          >
            <Search className="w-5 h-5" />
            <span>Find my photos</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-center mt-3" style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
            Take a quick selfie and we'll pull out every photo you're in.
          </p>
        </div>
      </div>

      {/* Toolbar: count + sort */}
      <div className="flex items-center justify-between gap-[14px] flex-wrap" style={{ padding: 'clamp(18px,2.5vw,24px) clamp(16px,3vw,38px) clamp(14px,2vw,18px)' }}>
        {collections.length > 0 ? (
          <CollectionPills collections={collections} activeSlug={selectedCollection} onSelect={(slug) => handleCollectionChange(slug)} totalCount={photos.length} totalLabel="All" />
        ) : (
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
            {displayPhotos.length < photos.length ? (
              <>Showing <span style={{ fontWeight: 700, color: '#fff' }}>{displayPhotos.length}</span> of {photos.length}
                <button onClick={clearFaceSearch} className="ml-3 font-medium" style={{ color: '#BF212F', cursor: 'pointer' }}>Show all</button>
              </>
            ) : (<><span style={{ fontWeight: 700, color: '#fff' }}>{photos.length}</span> photos</>)}
          </p>
        )}
        <div className="flex items-center gap-[10px] flex-shrink-0">
          {collections.length > 0 && displayPhotos.length < photos.length && (
            <button onClick={clearFaceSearch} className="font-medium" style={{ color: '#BF212F', fontSize: 13, cursor: 'pointer' }}>Show all</button>
          )}
          <button
            onClick={() => handleSortChange(sortOrder === 'newest' ? 'oldest' : 'newest')}
            className="font-tiq-heading inline-flex items-center gap-[7px]"
            style={{ padding: '9px 13px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.16)', background: 'transparent', color: '#cfcdc9', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5h10M11 9h7M11 13h4M3 17l3 3 3-3M6 4v16" /></svg>
            {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
          </button>
        </div>
      </div>

      {selectedCollection && (
        <div style={{ padding: '0 clamp(16px,3vw,38px)' }}>
          <button onClick={() => copyCollectionLink(selectedCollection)} className="inline-flex items-center gap-1.5 font-tiq-heading" style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}>
            <Share2 className="w-3.5 h-3.5" />
            {copiedCollectionLink ? 'Link copied!' : 'Copy direct link to this collection'}
          </button>
        </div>
      )}

      {/* Photo Grid — Cinema masonry, same as the flagship share link. Each tile
          is the photo's natural box, so the frame overlay stretches to exactly
          the photo (mirrors the lightbox and the server composite). */}
      <div style={{ padding: '6px clamp(16px,3vw,38px) clamp(40px,6vw,64px)' }}>
        {displayPhotos.length === 0 ? (
          <div className="text-center" style={{ padding: '80px 20px' }}>
            <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
              {photos.length === 0 ? 'No photos in this gallery yet' : 'No matching photos found'}
            </p>
          </div>
        ) : (
          <MasonryGrid
            photos={displayPhotos}
            onPhotoClick={(_photo, index) => openLightbox(index)}
            onDownload={(photo) => downloadPhoto(photo)}
            backendUrl={BACKEND_URL}
            protectImages
            frameUrlFor={getFrameUrl}
            onImageLoad={handleGridImageLoad}
          />
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && displayPhotos[currentPhotoIndex] && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-2xl">
          {/* Top Controls */}
          <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/70 to-transparent p-4">
            <div className="flex items-center justify-between">
              <div className="bg-white/10 backdrop-blur-xl text-white px-4 py-2 rounded-2xl text-sm font-medium">
                {currentPhotoIndex + 1} / {displayPhotos.length}
              </div>
              <button onClick={closeLightbox} className="bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl p-2.5 transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main Image with Frame.
              Note: photo IMG must NOT use `object-contain` here. Without object-fit,
              the IMG element naturally sizes to its image (capped by max-w/max-h while
              keeping aspect ratio), so the IMG's CSS box matches the visible pixels
              exactly. The absolute-positioned frame then covers exactly that area. */}
          <div
            className="absolute left-0 right-0 flex items-center justify-center"
            style={{ top: '70px', bottom: '120px', padding: '8px' }}
          >
            {(() => {
              const photo = displayPhotos[currentPhotoIndex];
              const frameUrl = getLightboxFrameUrl(photo);
              return (
                <div className="relative inline-flex">
                  <img
                    src={photo.preview_url || photo.original_url}
                    alt={photo.filename || `Photo ${currentPhotoIndex + 1}`}
                    className="block select-none"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    onLoad={(e) => handleLightboxImageLoad(photo.id, e)}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 'calc(100vh - 200px)',
                      width: 'auto',
                      height: 'auto',
                      WebkitTouchCallout: 'none',
                    }}
                  />
                  {frameUrl && (
                    <img
                      src={frameUrl}
                      alt=""
                      className="absolute inset-0 pointer-events-none"
                      {...noSaveImgProps}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'fill',
                      }}
                    />
                  )}
                </div>
              );
            })()}
          </div>

          {/* Navigation Arrows */}
          {displayPhotos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-40 bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center transition-all"
              >
                <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-40 bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center transition-all"
              >
                <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            </>
          )}

          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black/70 to-transparent p-4 pb-6">
            <div className="flex justify-center mb-3">
              <button
                onClick={(e) => downloadPhoto(displayPhotos[currentPhotoIndex], e)}
                disabled={downloading === displayPhotos[currentPhotoIndex].id}
                className="bg-gradient-to-r from-[#BF212F] to-[#F26B38] hover:opacity-90 text-white px-6 py-3 rounded-2xl flex items-center gap-2 transition-all shadow-lg font-medium"
              >
                {downloading === displayPhotos[currentPhotoIndex].id ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Download className="w-5 h-5" />
                )}
                <span>Download with Frame</span>
              </button>
            </div>
            <p className="text-center text-white/80 text-sm truncate px-4">
              {String(displayPhotos[currentPhotoIndex]?.filename || '')}
            </p>
          </div>
        </div>
      )}

      {/* Download progress modal — only shown when on-the-fly compositing is required */}
      {downloadingPhoto && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Photo preview with shimmering frame placeholder */}
            <div className="relative aspect-[4/5] bg-slate-100 overflow-hidden">
              <img
                src={downloadingPhoto.preview_url || downloadingPhoto.thumbnail_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)',
                  animation: 'tiq-shimmer 1.6s linear infinite',
                  backgroundSize: '200% 100%'
                }}
              />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-slate-700 shadow">
                <Sparkles className="w-3.5 h-3.5 text-[#BF212F]" />
                Branded
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#BF212F] to-[#F26B38] mb-3 shadow-lg shadow-red-500/30">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Preparing your branded photo</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                We're applying the frame to your high-resolution image. Your download will start in a moment.
              </p>
              <div className="mt-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#BF212F] via-[#F26B38] to-[#BF212F]"
                  style={{
                    width: '40%',
                    backgroundSize: '200% 100%',
                    animation: 'tiq-progress-slide 1.4s ease-in-out infinite'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <CinemaFooter />

      {/* Keyframes for the modal animations */}
      <style>{`
        @keyframes tiq-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes tiq-progress-slide {
          0% { transform: translateX(-110%); }
          100% { transform: translateX(260%); }
        }
      `}</style>
    </div>
  );
};

export default BrandedGallery;
